/**
 * ColorUtils — shared colour packing helpers.
 *
 * Single source of truth for converting RGBA colour objects into the uint32
 * **ABGR** layout used by the SoA sprite storages and GPU vertex colours.
 * Previously `packColorABGR` was duplicated verbatim in `core/SpriteSync` and
 * `player/SpriteSync`.
 */

/** An RGBA colour with 0-255 channels. */
export interface RGBA {
  r: number;
  g: number;
  b: number;
  a: number;
}

/**
 * Pack an RGBA colour object (0-255 per channel) into a single uint32 (ABGR
 * layout). A number is returned unchanged (already packed); `undefined`
 * yields opaque white (`0xffffffff`).
 */
export function packColorABGR(color: RGBA | number | undefined): number {
  if (typeof color === 'number') return color;
  if (!color) return 0xffffffff;
  const r = Math.max(0, Math.min(255, color.r ?? 255));
  const g = Math.max(0, Math.min(255, color.g ?? 255));
  const b = Math.max(0, Math.min(255, color.b ?? 255));
  const a = Math.max(0, Math.min(255, color.a ?? 255));
  return ((a & 0xff) << 24) | ((b & 0xff) << 16) | ((g & 0xff) << 8) | (r & 0xff);
}
